import React, { forwardRef } from 'react';

export interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  helperText?: string;
  error?: string;
  selectSize?: 'sm' | 'md' | 'lg';
  options?: Array<{ value: string; label: string; disabled?: boolean }>;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      label,
      helperText,
      error,
      selectSize = 'md',
      options,
      className = '',
      id,
      required,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const selectId = id || `select-${Math.random().toString(36).substr(2, 9)}`;
    const errorId = `${selectId}-error`;
    const helperTextId = `${selectId}-helper`;

    // Size-based padding — matches Input/Textarea's compact scale
    const sizeClasses = {
      sm: 'px-3 py-2 text-xs',
      md: 'px-3.5 py-2.5 text-sm',
      lg: 'px-4 py-3 text-sm',
    };

    // Base select classes — matches the site's flat, sharp-cornered form language
    const baseSelectClasses = `
      w-full
      border
      bg-white
      text-[#404040]
      transition-colors duration-200
      focus:outline-none
      focus:border-[#E85C27]
      disabled:bg-[#F0EDE8]
      disabled:text-[#A3A3A3]
      disabled:cursor-not-allowed
      appearance-none
      bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20fill%3D%22none%22%20viewBox%3D%220%200%2020%2020%22%3E%3Cpath%20stroke%3D%22%23A3A3A3%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%20stroke-width%3D%221.5%22%20d%3D%22m6%208%204%204%204-4%22%2F%3E%3C%2Fsvg%3E')]
      bg-[length:1.25rem]
      bg-[right_0.5rem_center]
      bg-no-repeat
      pr-10
      ${error ? 'border-red-400 focus:border-red-400' : 'border-[#E8E6E2]'}
      ${sizeClasses[selectSize]}
      ${className}
    `.trim().replace(/\s+/g, ' ');

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={selectId}
            className="block text-xs uppercase tracking-wider text-[#A3A3A3] mb-1.5"
          >
            {label}
            {required && <span className="text-red-400 ml-1">*</span>}
          </label>
        )}

        <select
          ref={ref}
          id={selectId}
          className={baseSelectClasses}
          disabled={disabled}
          required={required}
          aria-invalid={!!error}
          aria-describedby={
            error ? errorId : helperText ? helperTextId : undefined
          }
          {...props}
        >
          {options
            ? options.map((option) => (
                <option
                  key={option.value}
                  value={option.value}
                  disabled={option.disabled}
                >
                  {option.label}
                </option>
              ))
            : children}
        </select>

        {error && (
          <p id={errorId} className="text-xs text-red-500 mt-1">
            {error}
          </p>
        )}

        {helperText && !error && (
          <p id={helperTextId} className="text-xs text-[#6B6B6B] mt-1">
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';
